/* eslint-disable @typescript-eslint/camelcase */
import { useState, useCallback, useMemo } from 'react';

// @ts-ignore
import ErrorHandler from 'store/utils/ErrorHandler';
// @ts-ignore
import messages from 'constants/messages';
// @ts-ignore
import Notify from 'store/utils/Notify';
import {
  commitBranch,
  pullBranch,
  fetchBranches,
  createBranch,
  checkGitStatus,
  checkForConflicts,
  deleteBranch,
} from './actions';
import {
  VersionControlType,
  CommitRequest,
  PullRequest,
  FetchBranchesRequest,
  Branch,
  CreateBranchRequest,
  GitStatusRequest,
  DeleteBranchRequest,
} from './types';

const useVersionControl: VersionControlType = type => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<Array<Branch>>([]);

  const resetError = () => setError('');

  const commitRequest: CommitRequest = useCallback(
    async (message, onSuccess) => {
      setLoading(true);

      try {
        await commitBranch(message);
        setLoading(false);

        if (onSuccess) {
          onSuccess();
        }

        Notify.success({
          title: 'Changes uploaded successfully!',
        });
      } catch (e) {
        setLoading(false);
        setError(e.message);
      }
    },
    [],
  );

  const pullRequest: PullRequest = useCallback(async onSuccess => {
    setLoading(true);

    try {
      const { data } = await pullBranch();
      setLoading(false);

      if (onSuccess) {
        onSuccess(data);
      }

      Notify.success({
        title: 'Changes fetched successfully!',
      });

      return data;
    } catch (e) {
      const { data } = e?.response || {};

      setLoading(false);
      setError(data?.detail || e.message);

      return data;
    }
  }, []);

  const fetchBranchesRequest: FetchBranchesRequest = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await fetchBranches();
      setData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createBranchRequest: CreateBranchRequest = useCallback(
    async (parentBranch, branchName, onSuccess) => {
      setLoading(true);

      try {
        const { data } = await createBranch(parentBranch, branchName);

        setLoading(false);

        if (onSuccess) {
          onSuccess();
        }

        Notify.success({
          title: data.message,
        });
      } catch (e) {
        setError(ErrorHandler.getNotificationError(e));
        setLoading(false);
      }
    },
    [],
  );

  const onPullAction = async (onSuccess?: () => void) => {
    const {
      data: { conflicts },
    } = await checkForConflicts();

    if (conflicts.length > 0) {
      setError(messages.VERSION_CONTROL.MODAL.GIT_STATUS.ERROR_TITLE_CONFLICTS);
    } else if (onSuccess) {
      onSuccess();
    }
  };

  const onCommitAndPushAction = async (onSuccess?: () => void) => {
    const {
      data: { commits_behind },
    } = await checkGitStatus();

    if (commits_behind > 0) {
      setError(messages.VERSION_CONTROL.MODAL.GIT_STATUS.ERROR_TITLE_FETCH);
    } else if (onSuccess) {
      onSuccess();
    }
  };

  const checkGitStatusRequest: GitStatusRequest = useCallback(
    async (currentAction: string, onSuccess) => {
      setLoading(true);

      try {
        if (currentAction === 'pull') {
          await onPullAction(onSuccess);
        } else if (currentAction === 'commit') {
          await onCommitAndPushAction(onSuccess);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteBranchRequest: DeleteBranchRequest = useCallback(
    async (account, branchName) => {
      setLoading(true);
      try {
        await deleteBranch(account, branchName);
      } catch (e) {
        setError(e.message);
        Notify.error({
          title: 'Failed to delete branch!',
          description: ErrorHandler.getNotificationError(e),
        });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const actions = useMemo(
    () => ({
      commit: commitRequest,
      gitStatus: checkGitStatusRequest,
      pull: pullRequest,
      branches: fetchBranchesRequest,
      createBranch: createBranchRequest,
      deleteBranch: deleteBranchRequest,
    }),
    [
      commitRequest,
      pullRequest,
      checkGitStatusRequest,
      fetchBranchesRequest,
      createBranchRequest,
      deleteBranchRequest,
    ],
  );

  return { loading, error, resetError, data, action: actions[type] };
};

export default useVersionControl;
